import { Box, FormControl, Text, VStack } from '@chakra-ui/react';
import type { MultiValue } from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import {
  DEFAULT_PRIMARY_COMPANIES,
  DEFAULT_PRIMARY_CUSTOM_ROLE,
  DEFAULT_PRIMARY_GEOGRAPHY,
} from '../../../next/CustomDash/filters/contexts/defaultFilters';
import type { TreeSelection } from '../../../next/CustomDash/filters/types/filters';
import { getRoleDefaultSelectionsForTaxonomy } from '../../../next/CustomDash/filters/utils/getRoleDefaultSelectionsForTaxonomy';
import {
  EntitySearchSelect,
  MIN_SEARCH_LENGTH,
  type EntitySearchOption as Option,
  treeSelectionToEntitySearchOption,
  useCompanyEntitySearchConfig,
  useGeographyEntitySearchConfig,
  useRoleEntitySearchConfig,
} from '../../components/entity-search-select';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { PreferenceScope, UserPreferenceKey } from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.DefaultFilters,
  label: 'Default Filters',
};

type SelectConfig = {
  options: Option[];
  isLoading: boolean;
  onInputChange?: (value: string) => void;
  searchTerm?: string;
  searchResultsCount?: number;
};

type SelectProps = SelectConfig & {
  title: string;
  placeholder: string;
  tooltip: string;
  storedSelections: TreeSelection[];
  initialSelections: TreeSelection[];
  indicateDiff: boolean;
  onChange?: (value: MultiValue<Option>) => void;
  noOptionsMessage?: () => string;
};

type ScopedEditor = {
  storedSelections: TreeSelection[];
  initialSelections: TreeSelection[];
  affectedScope: PreferenceScope;
  indicateDiff: boolean;
  roleTaxonomySelection: CustomRoleTaxonomySelection;
  onPreferenceUpdate?: PreferenceEditorProps['onPreferenceUpdate'];
  onChange: (vals: MultiValue<Option>) => void;
};

const EMPTY_SELECTIONS: TreeSelection[] = [];

const normaliseStoredSelections = (
  raw:
    | TreeSelection[]
    | Partial<Record<PreferenceScope, TreeSelection[]>>
    | undefined,
  scope: PreferenceScope
): TreeSelection[] => {
  if (!raw) return EMPTY_SELECTIONS;
  if (Array.isArray(raw)) return raw;
  return raw[scope] ?? EMPTY_SELECTIONS;
};

export default function DefaultFiltersEditor({
  affectedScope,
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const roleTaxonomySelection = useMemo<CustomRoleTaxonomySelection>(() => {
    const rolePref = preferences[UserPreferenceKey.Taxonomies]?.role;
    if (!rolePref || rolePref === 'adaptive') return { default: true };
    return rolePref;
  }, [preferences]);

  const storedSelections = normaliseStoredSelections(
    preferences[UserPreferenceKey.DefaultFilters] as
      | TreeSelection[]
      | Partial<Record<PreferenceScope, TreeSelection[]>>
      | undefined,
    affectedScope
  );

  const initialSelections = useMemo(() => {
    if (storedSelections.length > 0) return storedSelections;

    switch (affectedScope) {
      case PreferenceScope.COMPANY:
        return DEFAULT_PRIMARY_COMPANIES;
      case PreferenceScope.GEOGRAPHY:
        return DEFAULT_PRIMARY_GEOGRAPHY;
      case PreferenceScope.ROLE: {
        const taxonomyDefaults = getRoleDefaultSelectionsForTaxonomy(
          roleTaxonomySelection
        );
        return taxonomyDefaults.length > 0
          ? taxonomyDefaults
          : DEFAULT_PRIMARY_CUSTOM_ROLE;
      }
      default:
        return [];
    }
  }, [affectedScope, roleTaxonomySelection, storedSelections]);

  const indicateDiff =
    diff?.includes(UserPreferenceKey.DefaultFilters) ?? false;

  const onChange = useCallback(
    (vals: MultiValue<Option>) => {
      const updated = (vals ?? []).map((o) => o.data);
      PreferenceBrowserStorage.update(
        affectedScope,
        UserPreferenceKey.DefaultFilters,
        updated
      );
      onPreferenceUpdate?.(UserPreferenceKey.DefaultFilters, updated);
    },
    [affectedScope, onPreferenceUpdate]
  );

  const commonProps = {
    storedSelections,
    initialSelections,
    indicateDiff,
    affectedScope,
    roleTaxonomySelection,
    onPreferenceUpdate,
    onChange,
  } as const;

  let ScopedEditor: React.FC<ScopedEditor> = () => null;
  switch (affectedScope) {
    case PreferenceScope.COMPANY:
      ScopedEditor = CompanyDefaults;
      break;
    case PreferenceScope.GEOGRAPHY:
      ScopedEditor = GeographyDefaults;
      break;
    case PreferenceScope.ROLE:
      ScopedEditor = RoleDefaults;
      break;
  }

  return (
    <VStack align="stretch" spacing={3} width="100%">
      <FormControl>
        <ScopedEditor {...commonProps} />
      </FormControl>
    </VStack>
  );
}

function SelectBase({
  storedSelections,
  initialSelections,
  indicateDiff,
  onChange,
  ...props
}: SelectProps) {
  /**
   * The field will never be empty on initial load:
   * it displays either the user's previous selections or the initial values.
   *
   * We intentionally prevent clearing the last selection so that the defaults
   * don't silently "snap back" to the initial values on reopen.
   */

  const getValues = useCallback(() => {
    return (storedSelections.length ? storedSelections : initialSelections).map(
      treeSelectionToEntitySearchOption
    );
  }, [storedSelections, initialSelections]);

  const [value, setValue] = useState<Option[]>(getValues());

  // It helps to reset the value
  useEffect(() => {
    if (!indicateDiff) {
      setValue(getValues());
    }
  }, [indicateDiff, getValues]);

  const handleChange = useCallback(
    (vals: MultiValue<Option>) => {
      // Prevent clearing the last selection (we must always have at least one default)
      if (initialSelections.length > 0 && vals.length === 0) {
        return;
      }
      setValue([...vals]);
      onChange?.(vals);
    },
    [initialSelections.length, onChange]
  );

  const testId = useMemo(() => {
    return `default-filters-select-${props.title.toLowerCase().replace(' ', '-')}`;
  }, [props.title]);

  return (
    <>
      <PreferenceEditorSectionHeading
        title={props.title}
        indicateDiff={indicateDiff}
        tooltip={props.tooltip}
      />
      <Box data-testid={testId} mb={2} maxW="520px">
        <EntitySearchSelect<Option>
          minSelectedValues={1}
          isLoading={props.isLoading}
          options={props.options}
          value={value}
          onChange={handleChange}
          onInputChange={props.onInputChange}
          placeholder={props.placeholder}
          noOptionsMessage={props.noOptionsMessage}
        />
      </Box>
      {value.length === 1 && (
        <Text fontSize="xs" color="gray.500">
          At least 1 default selection is required
        </Text>
      )}
    </>
  );
}

// ---------------- Scoped Editor Components -----------------

function CompanyDefaults({
  storedSelections,
  initialSelections,
  indicateDiff,
  onChange,
}: ScopedEditor) {
  const companyCfg = useCompanyEntitySearchConfig(storedSelections);

  return (
    <SelectBase
      {...companyCfg}
      title="Default Companies"
      placeholder="Search companies or industries"
      tooltip="Company filter options automatically display when opening Company dashboards"
      storedSelections={storedSelections}
      initialSelections={initialSelections}
      indicateDiff={indicateDiff}
      onChange={onChange}
      noOptionsMessage={() => {
        const isSearching =
          companyCfg.searchTerm &&
          companyCfg.searchTerm.trim().length >= MIN_SEARCH_LENGTH;
        if (!isSearching) {
          return 'Start typing to see results...';
        }
        return 'No matches found';
      }}
    />
  );
}

function GeographyDefaults({
  storedSelections,
  initialSelections,
  indicateDiff,
  onChange,
}: ScopedEditor) {
  const geoCfg = useGeographyEntitySearchConfig(storedSelections);

  return (
    <SelectBase
      {...geoCfg}
      title="Default Geographies"
      placeholder="Search geographies"
      tooltip="Geography filter options automatically display when opening Geography dashboards"
      storedSelections={storedSelections}
      initialSelections={initialSelections}
      indicateDiff={indicateDiff}
      onChange={onChange}
    />
  );
}

function RoleDefaults({
  storedSelections,
  initialSelections,
  indicateDiff,
  roleTaxonomySelection,
  onChange,
}: ScopedEditor) {
  const roleCfg = useRoleEntitySearchConfig(
    storedSelections,
    roleTaxonomySelection
  );

  return (
    <SelectBase
      {...roleCfg}
      title="Default Roles"
      placeholder="Search roles"
      tooltip="Role filter options automatically display when opening Role dashboards"
      storedSelections={storedSelections}
      initialSelections={initialSelections}
      indicateDiff={indicateDiff}
      onChange={onChange}
    />
  );
}
